(() => {
  'use strict';
  const MAX_BYTES = 512 * 1024;
  const MAX_PUBLICATION_AGE_MS = 26 * 3600000;
  // Same formatter as the Ref section, so a Ref median reads identically on both pages.
  const money = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 2 });
  const labels = { available: '観測値あり', stale: '古いデータ', missing: '未取得', ambiguous: '対応確認中' };
  const sha = async (raw) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', raw)), (n) => n.toString(16).padStart(2, '0')).join('');
  const validDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
  const count = (value) => Number.isSafeInteger(value) && value >= 0;
  const amount = (value) => Number.isFinite(value) && value >= 0;
  const pagePattern = (kind) => new RegExp(`^/collections/${kind}-[a-z0-9][a-z0-9-]{0,250}$`);

  function assetUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || ![window.location.origin, 'https://cdn.shopify.com'].includes(url.origin) || !/\/ts-hierarchy-market-[a-f0-9]{64}\.json$/.test(url.pathname)) return null;
      return url.href;
    } catch { return null; }
  }
  async function boundedBytes(response, expected) {
    if (Number(response.headers.get('Content-Length')) > MAX_BYTES) throw new Error('size');
    if (!response.body?.getReader) {
      const raw = new Uint8Array(await response.arrayBuffer());
      if (raw.length !== expected || raw.length > MAX_BYTES) throw new Error('size');
      return raw;
    }
    const reader = response.body.getReader(), chunks = [];
    let size = 0;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > expected || size > MAX_BYTES) { await reader.cancel(); throw new Error('size'); }
        chunks.push(value);
      }
    } finally { reader.releaseLock(); }
    if (size !== expected) throw new Error('size');
    const raw = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) { raw.set(chunk, offset); offset += chunk.byteLength; }
    return raw;
  }
  function validPrice(point, id) {
    if (!point || point.reference_id !== id || point.currency !== 'JPY' || !Object.hasOwn(labels, point.status) || (point.snapshot_date !== null && !validDay(point.snapshot_date)) || point.sample_count !== null) return false;
    const priceKeys = ['price_min', 'price_median', 'price_max'];
    const countKeys = ['listing_count', 'in_stock_count'];
    if (priceKeys.some((key) => point[key] !== null && !amount(point[key])) || countKeys.some((key) => point[key] !== null && !count(point[key]))) return false;
    if (point.status === 'ambiguous' && [...priceKeys, ...countKeys].some((key) => point[key] !== null)) return false;
    if (['available', 'stale'].includes(point.status) && (point.price_method !== 'source_approximate_median' || point.aggregate_status !== 'single_source_group' || !validDay(point.snapshot_date) || priceKeys.some((key) => point[key] === null) || point.price_min > point.price_median || point.price_median > point.price_max || point.listing_count === null)) return false;
    return true;
  }
  // Group values never include a median. Anything else is refused, not relabelled.
  function validRollup(value, point = false) {
    if (!value || !['observed_reference_count', 'ambiguous_reference_count', 'incomplete_reference_count'].every((key) => count(value[key])) || 'price_median' in value) return false;
    if (value.observed_reference_count > 0) return count(value.listing_count) && amount(value.price_min) && amount(value.price_max) && value.price_min <= value.price_max && amount(value.reference_median_min) && amount(value.reference_median_max) && value.price_min <= value.reference_median_min && value.reference_median_min <= value.reference_median_max && value.reference_median_max <= value.price_max && (value.in_stock_count === null || (count(value.in_stock_count) && value.in_stock_count <= value.listing_count));
    return !point && ['listing_count', 'in_stock_count', 'price_min', 'price_max', 'reference_median_min', 'reference_median_max'].every((key) => value[key] === null);
  }
  function validLatest(value, asOf) {
    return validRollup(value) && ['available', 'missing'].includes(value.status) && value.market_date === asOf && value.currency === 'JPY' && Number.isSafeInteger(value.reference_count) && value.reference_count >= 1 && count(value.unobserved_reference_count)
      && value.observed_reference_count + value.ambiguous_reference_count + value.incomplete_reference_count + value.unobserved_reference_count === value.reference_count
      && (value.status === 'available') === (value.observed_reference_count > 0);
  }
  async function validate(data, host) {
    const expected = host.dataset, now = Date.now();
    const expiry = Date.parse(data?.expires_at), pageExpiry = Date.parse(expected.expires);
    const snapshot = data?.snapshot, group = data?.group, aggregation = data?.aggregation;
    // The same source binding is required for historical series after current values expire.
    if (data?.schema_version !== 'market-hierarchy-data/v1' || !Number.isFinite(expiry) || !Number.isFinite(pageExpiry) || expiry < pageExpiry || snapshot?.snapshot_id !== expected.snapshotId || snapshot.schema_version !== 1 || snapshot.freshness !== 'fresh' || snapshot.source_completeness !== 'unverified' || !validDay(snapshot.as_of) || aggregation?.method !== 'reference_rollup_v1' || aggregation.group_median !== 'not_provided' || aggregation.currency !== 'JPY') throw new Error('contract');
    const generated = Date.parse(data.generated_at), verified = Date.parse(data.links_verified_at), snapshotGenerated = Date.parse(snapshot.generated_at), asOf = Date.parse(`${snapshot.as_of}T00:00:00+09:00`);
    if (![generated, verified, snapshotGenerated, asOf, now].every(Number.isFinite) || generated > now + 60000 || verified > now + 60000 || snapshotGenerated > now + 60000 || asOf > now + 60000 || expiry <= generated || expiry > Math.min(generated + MAX_PUBLICATION_AGE_MS, verified + MAX_PUBLICATION_AGE_MS, snapshotGenerated + 172800000, asOf + 172800000)) throw new Error('time');
    if (!['brand', 'model'].includes(group?.level) || group.level !== expected.level || group.canonical_url !== expected.canonicalUrl || group.canonical_url !== `/collections/${group.handle}` || !pagePattern(group.level).test(group.canonical_url) || await sha(new TextEncoder().encode(`${group.level}:${group.handle}`)) !== expected.groupKey) throw new Error('identity');
    if (!validLatest(data.latest, snapshot.as_of)) throw new Error('latest');
    const references = data.references, brand = group.level === 'brand';
    if (!references || references.selection !== (brand ? 'observed_on_market_date_by_listing_count' : 'all_references') || references.total !== (brand ? data.latest.observed_reference_count : data.latest.reference_count) || !Array.isArray(references.items) || references.items.length > references.total || references.items.length > 2000 || references.truncated !== references.total > references.items.length) throw new Error('references');
    for (const item of references.items) {
      if (typeof item?.reference_id !== 'string' || !item.reference_id || typeof item.canonical_ref !== 'string' || !item.canonical_ref || !pagePattern('ref').test(item.canonical_url) || !validPrice(item.market_price, item.reference_id)) throw new Error('reference');
      if (brand && (item.market_price.status !== 'available' || item.market_price.snapshot_date !== snapshot.as_of)) throw new Error('reference');
      // A listed available price never outlives its own market day + 48 hours.
      if (item.market_price.status === 'available' && expiry > Date.parse(`${item.market_price.snapshot_date}T00:00:00+09:00`) + 172800000) throw new Error('time');
    }
    if (group.level === 'brand') {
      if (!Array.isArray(data.models) || data.models.length > 5000) throw new Error('models');
      for (const model of data.models) {
        if (typeof model?.title !== 'string' || !model.title || model.canonical_url !== `/collections/${model.handle}` || !pagePattern('model').test(model.canonical_url) || !validLatest(model.latest, snapshot.as_of)) throw new Error('model');
      }
    } else if ('models' in data) throw new Error('models');
    const series = data.series;
    if (series?.currency !== 'JPY' || series.gaps !== 'omitted' || !validDay(series.start) || series.end !== snapshot.as_of || series.start > series.end || Date.parse(series.end) - Date.parse(series.start) > 89 * 86400000 || !Array.isArray(series.items) || series.items.length > 90) throw new Error('series');
    let prior = '';
    for (const point of series.items) {
      if (!validDay(point?.snapshot_date) || point.snapshot_date <= prior || point.snapshot_date < series.start || point.snapshot_date > series.end || !(point.observed_reference_count >= 1) || !validRollup(point, true)) throw new Error('point');
      prior = point.snapshot_date;
    }
    return data;
  }
  function cell(tag, value) {
    const node = document.createElement(tag);
    node.textContent = value;
    return node;
  }
  function pageCell(title, url, kind) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'row');
    if (pagePattern(kind).test(url)) {
      const link = cell('a', title);
      link.setAttribute('href', url);
      th.append(link);
    } else th.textContent = title;
    return th;
  }
  // The spread of Ref approximate medians (lowest to highest), never a group median.
  const range = (value) => value.observed_reference_count > 0 ? `${money.format(value.reference_median_min)}〜${money.format(value.reference_median_max)}` : '—';
  const listings = (value) => value.observed_reference_count > 0 ? `${value.listing_count}件` : '—';
  // Phones stack each row (CSS). Explicit roles keep the table semantics when the
  // display changes, and data-label gives each stacked value its column name.
  function table(caption, headings, rows) {
    const node = document.createElement('table');
    // A class keeps Dawn's global table:not([class]) padding, borders and fixed layout off.
    node.setAttribute('class', 'ts-hierarchy-market__table');
    node.setAttribute('role', 'table');
    node.append(cell('caption', caption));
    const thead = document.createElement('thead'), head = document.createElement('tr');
    thead.setAttribute('role', 'rowgroup'); head.setAttribute('role', 'row');
    for (const label of headings) { const th = cell('th', label); th.setAttribute('scope', 'col'); th.setAttribute('role', 'columnheader'); head.append(th); }
    thead.append(head); node.append(thead);
    const body = document.createElement('tbody');
    body.setAttribute('role', 'rowgroup');
    for (const tr of rows) {
      Array.from(tr.children).forEach((child, index) => { if (index > 0) child.setAttribute('data-label', headings[index]); });
      body.append(tr);
    }
    node.append(body);
    return node;
  }
  function row(first, values) {
    const tr = document.createElement('tr');
    tr.setAttribute('role', 'row');
    first.setAttribute('role', 'rowheader');
    tr.append(first);
    for (const value of values) { const td = cell('td', value); td.setAttribute('role', 'cell'); tr.append(td); }
    return tr;
  }
  const renderers = {
    // Model pages only; Brand pages show per-model values on the directory rows instead.
    references: (data) => data.group.level === 'model' && data.references.items.length ? table(`全${data.references.total}型番`, ['型番', '近似中央値', '掲載数', '状態', '最終観測日'],
      data.references.items.map((item) => {
        const price = item.market_price, available = price.status === 'available';
        return row(pageCell(item.canonical_ref, item.canonical_url, 'ref'), [available ? money.format(price.price_median) : '—', available ? `${price.listing_count}件` : '—', labels[price.status], price.snapshot_date || '—']);
      })) : null,
    series: (data) => data.series.items.length ? table(`${data.series.start}〜${data.series.end}の観測値`, ['観測日', '掲載数の合計', '型番ごとの中央値の範囲'],
      [...data.series.items].reverse().map((point) => { const th = cell('th', point.snapshot_date); th.setAttribute('scope', 'row'); return row(th, [listings(point), range(point)]); })) : null,
  };
  function setup(host) {
    if (host.dataset.marketInitialized) return;
    host.dataset.marketInitialized = 'true';
    const container = host.querySelector('[data-market-details]');
    const details = host.querySelectorAll('[data-market-detail]');
    if (!container || !details.length) return;
    const expires = Date.parse(host.dataset.expires);
    const currentAvailable = host.dataset.marketStatusCode === 'available';
    let controller, timer, pending = null, verified = null;
    const expire = () => {
      if (Number.isFinite(expires) && Date.now() < expires) return false;
      clearTimeout(timer);
      const values = host.querySelector('[data-market-values]');
      if (values) values.hidden = true;
      const invalidDeadline = !Number.isFinite(expires);
      if (invalidDeadline) { controller?.abort(); container.hidden = true; }
      for (const detail of details) {
        if (detail.getAttribute('data-market-detail') !== 'series') detail.hidden = true;
      }
      const date = host.querySelector('[data-market-as-of]');
      if (date) date.hidden = false;
      host.querySelector('[data-market-status]').textContent = host.dataset.expiredMessage;
      // Observation methods and historical graphs remain alongside their original dates.
      // The directory rows come from the same group and expire with it.
      for (const row of document.querySelectorAll('[data-directory-market]')) row.hidden = true;
      return true;
    };
    const schedule = () => {
      if (expire()) return;
      clearTimeout(timer);
      timer = setTimeout(schedule, Math.min(expires - Date.now() + 1, 86400000));
    };
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('pageshow', schedule);
    schedule();
    async function load() {
      const url = assetUrl(host.dataset.assetUrl), expected = Number(host.dataset.assetBytes);
      if (!url || !Number.isSafeInteger(expected) || expected < 1 || expected > MAX_BYTES || !/^[a-f0-9]{64}$/.test(host.dataset.assetSha) || !globalThis.crypto?.subtle) throw new Error('config');
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(url, { credentials: 'omit', signal: controller.signal, redirect: 'error' });
        if (!response.ok || response.redirected || (response.url && assetUrl(response.url) !== url)) throw new Error('response');
        const raw = await boundedBytes(response, expected);
        if (await sha(raw) !== host.dataset.assetSha) throw new Error('integrity');
        return await validate(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(raw)), host);
      } finally { clearTimeout(timeout); }
    }
    for (const detail of details) {
      if (detail.querySelector('[data-history-ssr]')) continue;
      const kind = detail.getAttribute('data-market-detail'), status = detail.querySelector('[data-detail-status]');
      let rendered = false;
      detail.addEventListener('toggle', async () => {
        const expired = expire();
        if (!detail.open || rendered || !Object.hasOwn(renderers, kind) || !status || !Number.isFinite(expires) || kind !== 'series' && (!currentAvailable || expired)) return;
        status.textContent = host.dataset.loadingMessage;
        try {
          pending ??= load();
          verified = await pending;
          const expiredAfterLoad = expire();
          if (kind !== 'series' && (!currentAvailable || expiredAfterLoad)) return;
          const content = renderers[kind](verified);
          if (content) { detail.querySelector('[data-detail-content]').replaceChildren(content); status.textContent = ''; }
          else status.textContent = host.dataset.emptyMessage;
          rendered = true;
        } catch {
          pending = null;
          if (!expire() || kind === 'series') status.textContent = host.dataset.errorMessage;
        }
      });
    }
  }
  // A Brand row can end before the Brand group (it follows its own Model group's deadline).
  // Rows are checked at their exact deadline; a legend with no visible row left is hidden too.
  let rowTimer;
  function expireRows() {
    const now = Date.now() / 1000;
    let next = Infinity;
    const dated = (scope) => [...scope.querySelectorAll('[data-expires-epoch]')].filter((row) => row.getAttribute('data-directory-market') !== null);
    for (const row of dated(document)) {
      const epoch = Number(row.getAttribute('data-expires-epoch'));
      if (!(epoch > now)) row.hidden = true;
      else next = Math.min(next, epoch);
    }
    for (const legend of document.querySelectorAll('[data-directory-legend]')) {
      const list = legend.closest?.('[data-ts-entity-directory]');
      const rows = list ? dated(list) : [];
      if (rows.length && rows.every((row) => row.hidden)) legend.hidden = true;
    }
    clearTimeout(rowTimer);
    if (Number.isFinite(next)) rowTimer = setTimeout(expireRows, Math.min((next - now) * 1000 + 1, 86400000));
  }
  function init() {
    document.querySelectorAll('[data-ts-hierarchy-market]').forEach(setup);
    expireRows();
    if (!init.watching) {
      init.watching = true;
      document.addEventListener('visibilitychange', expireRows);
      window.addEventListener('pageshow', expireRows);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
